import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Dialog, EmptyState, Field, Input, Select, Spinner, Switch, type Tone } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api, newIdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { minorToInput } from '../lib/money';
import { useStoreChoice } from '../lib/store-choice';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  Category,
  CollectionRow,
  ProductBulkDetail,
  ProductBulkOperation,
  ProductBulkPreview,
  Warehouse,
} from '../lib/types';

/**
 * Toplu ürün işlemleri (F5B-08): ürün listesinde seçim → işlem ve değerler → önizleme (önce/sonra, atlananlar
 * nedeniyle) → uygula. Büyük işlem arka planda uygulanır; geçmiş ekranında sonuç görülür ve geri alınır.
 */

const KINDS = [
  'status',
  'categories_add',
  'categories_remove',
  'tags_add',
  'tags_remove',
  'collection_add',
  'collection_remove',
  'stock_set',
  'stock_adjust',
  'seo',
  'merchant',
  'store_publish',
  'store_unpublish',
] as const;
type Kind = (typeof KINDS)[number];
const isStock = (k: string) => k === 'stock_set' || k === 'stock_adjust';
const isCategory = (k: string) => k === 'categories_add' || k === 'categories_remove';
const isTags = (k: string) => k === 'tags_add' || k === 'tags_remove';
const isCollection = (k: string) => k === 'collection_add' || k === 'collection_remove';
const isListing = (k: string) => k === 'store_publish' || k === 'store_unpublish';
/** Yayın engeli kodları (tekil yayındaki gerekçelerle aynı); atlanan satırın notu virgüllü olabilir. */
const BLOCKERS = new Set(['product_not_active', 'vet_approval_required', 'base_price_required']);

export interface BulkFilter {
  q?: string;
  status?: string;
  vetReview?: string;
  brandId?: string;
  tag?: string;
}
export type BulkSelection = { productIds: string[] } | { filter: BulkFilter };

const opTone: Record<string, Tone> = {
  preview: 'neutral',
  queued: 'amber',
  running: 'amber',
  applied: 'moss',
  failed: 'rose',
  reverted: 'slate',
  expired: 'neutral',
};
const itemTone: Record<string, Tone> = {
  pending: 'neutral',
  applied: 'moss',
  skipped: 'amber',
  reverted: 'slate',
  revert_skipped: 'amber',
};
const NOTES = new Set([
  'unchanged',
  'product_requires_active_variant',
  'too_many_categories',
  'too_many_tags',
  'lot_tracked',
  'insufficient_stock',
  'changed_since_preview',
]);

/** Kullanıcının hazırlayıp uygulayabileceği işlem türleri (işleme göre ayrı yetki). */
export function useBulkKinds(): Kind[] {
  const { can } = usePermissions();
  if (!can('catalog.bulk.apply')) return [];
  return KINDS.filter((k) =>
    isStock(k)
      ? can('inventory.stock.write')
      : isListing(k)
        ? can('catalog.product.publish')
        : can('catalog.product.write'),
  );
}

function useCategories(enabled: boolean) {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/v1/admin/categories'),
    enabled,
  });
}

function useNoteText() {
  const t = useT();
  return (note: string | null) => {
    if (!note) return '';
    if (NOTES.has(note)) return t(`admin.productBulk.notes.${note}`);
    const codes = note.split(',');
    if (codes.every((c) => BLOCKERS.has(c))) return codes.map((c) => t(`publishBlockers.${c}`)).join(' ');
    return t('admin.productBulk.notes.error', { code: note });
  };
}

/** Önizleme ve geçmişteki önce/sonra değerini okunur metne çevirir. */
function useValueText() {
  const t = useT();
  const cats = useCategories(true);
  return (kind: string, v: unknown): string => {
    if (kind === 'status') return t(`admin.products.statuses.${String(v)}`);
    if (isCategory(kind))
      return (v as string[]).map((id) => cats.data?.find((c) => c.id === id)?.name ?? '?').join(', ') || '—';
    if (isTags(kind)) return (v as string[]).join(', ') || '—';
    if (isCollection(kind))
      return v ? t('admin.productBulk.values.inCollection') : t('admin.productBulk.values.notInCollection');
    if (kind === 'merchant')
      return v ? t('admin.productBulk.values.included') : t('admin.productBulk.values.excluded');
    if (isListing(kind))
      return v ? t('admin.productBulk.values.published') : t('admin.productBulk.values.notPublished');
    if (kind === 'cells') {
      const c = v as Record<string, unknown>;
      return Object.entries(c)
        .map(([f, x]) => {
          const shown =
            x === null || x === ''
              ? '—'
              : f === 'status'
                ? t(`admin.productGrid.statuses.${String(x)}`)
                : f === 'priceMinor'
                  ? minorToInput(x as number)
                  : String(x);
          return `${t(f === 'priceMinor' ? 'admin.productGrid.cols.price' : `admin.productGrid.cols.${f}`)}: ${shown}`;
        })
        .join(' · ');
    }
    if (kind === 'seo') {
      const s = v as { seoTitle: string | null; seoDescription: string | null };
      return [s.seoTitle, s.seoDescription].map((x) => x || '—').join(' · ');
    }
    return String(v);
  };
}

// ---------------------------------------------------------------------------
// Ürün listesindeki seçim çubuğu
// ---------------------------------------------------------------------------

export function BulkBar({
  count,
  allMatching,
  canSelectAllMatching,
  onSelectAllMatching,
  onClear,
  selection,
}: {
  count: number;
  allMatching: boolean;
  canSelectAllMatching: boolean;
  onSelectAllMatching: () => void;
  onClear: () => void;
  selection: BulkSelection;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [applied, setApplied] = useState(false);
  return (
    <div
      role="region"
      aria-label={t('admin.productBulk.open')}
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-line bg-surface-2 px-4 py-2 text-sm"
    >
      <span className="font-medium" aria-live="polite">
        {allMatching ? t('admin.productBulk.allMatching') : t('admin.productBulk.selected', { count })}
      </span>
      {canSelectAllMatching && !allMatching && (
        <span className="inline-flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={onSelectAllMatching}>
            {t('admin.productBulk.selectAllMatching')}
          </Button>
          <HelpTip k="products.bulk_select_all" />
        </span>
      )}
      <span className="ms-auto inline-flex items-center gap-1">
        <Button size="sm" variant="ghost" onClick={onClear}>
          {t('admin.productBulk.clear')}
        </Button>
        <Button size="sm" variant="primary" onClick={() => setOpen(true)}>
          {t('admin.productBulk.open')}
        </Button>
        <HelpTip k="products.bulk" />
      </span>
      {open && (
        <BulkDialog
          selection={selection}
          onClose={() => {
            setOpen(false);
            // Uygulanan işlemin seçimi pencere kapanınca temizlenir (sonuç görünür kalsın).
            if (applied) onClear();
          }}
          onApplied={() => setApplied(true)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// İşlem penceresi: değerler → önizleme → uygula → sonuç
// ---------------------------------------------------------------------------

function BulkDialog({
  selection,
  onClose,
  onApplied,
}: {
  selection: BulkSelection;
  onClose: () => void;
  onApplied: () => void;
}) {
  const t = useT();
  const { formatDate } = useI18n();
  const qc = useQueryClient();
  const kinds = useBulkKinds();
  const [kind, setKind] = useState<Kind>(kinds[0] ?? 'status');
  const [status, setStatus] = useState('active');
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [tags, setTags] = useState('');
  const [collectionId, setCollectionId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [onlyEmpty, setOnlyEmpty] = useState(true);
  const [include, setInclude] = useState(true);
  const storeChoice = useStoreChoice();
  const [storeId, setStoreId] = useState('');
  const [preview, setPreview] = useState<ProductBulkPreview | null>(null);
  const [opId, setOpId] = useState<string | null>(null);
  const valueText = useValueText();
  const noteText = useNoteText();

  const cats = useCategories(isCategory(kind));
  const cols = useQuery({
    queryKey: ['collections'],
    queryFn: () => api.get<CollectionRow[]>('/v1/admin/collections'),
    enabled: isCollection(kind),
  });
  const whs = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => api.get<Warehouse[]>('/v1/admin/warehouses'),
    enabled: isStock(kind),
  });
  const manual = (cols.data ?? []).filter((c) => c.kind === 'manual');
  const activeWh = (whs.data ?? []).filter((w) => w.status === 'active');

  const params = () => {
    const qty = /^-?\d+$/.test(quantity.trim()) ? Number(quantity.trim()) : null;
    switch (kind) {
      case 'status':
        return { kind, status };
      case 'categories_add':
      case 'categories_remove':
        return { kind, categoryIds };
      case 'tags_add':
      case 'tags_remove':
        return {
          kind,
          tags: tags
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean),
        };
      case 'collection_add':
      case 'collection_remove':
        return { kind, collectionId: collectionId || null };
      case 'stock_set':
      case 'stock_adjust':
        return { kind, warehouseId: warehouseId || activeWh[0]?.id || null, quantity: qty };
      case 'seo':
        return {
          kind,
          seoTitle: seoTitle.trim() || null,
          seoDescription: seoDescription.trim() || null,
          onlyEmpty,
        };
      case 'merchant':
        return { kind, include };
      case 'store_publish':
      case 'store_unpublish':
        return { kind, storeId: storeId || storeChoice.storeId || null };
    }
  };

  const run = useApiMutation(
    () => api.post<ProductBulkPreview>('/v1/admin/products/bulk/preview', { selection, params: params() }),
    { onSuccess: (r) => setPreview(r), silentFieldErrors: true },
  );
  const apply = useApiMutation(
    (id: string) =>
      api.post<ProductBulkOperation>(`/v1/admin/products/bulk/${id}/apply`, undefined, {
        idempotencyKey: newIdempotencyKey(),
      }),
    {
      onSuccess: (r) => {
        setOpId(r.id);
        onApplied();
      },
    },
  );
  const result = useQuery({
    queryKey: ['product-bulk', opId],
    queryFn: async () => {
      const d = await api.get<ProductBulkDetail>(`/v1/admin/products/bulk/${opId}`);
      if (d.status === 'applied')
        await qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'product-bulk' });
      return d;
    },
    enabled: !!opId,
    refetchInterval: (q) =>
      q.state.data && q.state.data.status !== 'queued' && q.state.data.status !== 'running' ? false : 2000,
  });
  const err = (f: string) => fieldError(run.error, `params.${f}`);

  const footer = opId ? (
    <Button variant="primary" onClick={onClose}>
      {t('common.close')}
    </Button>
  ) : preview ? (
    <>
      <Button onClick={() => setPreview(null)}>{t('admin.productBulk.back')}</Button>
      <span className="inline-flex items-center gap-1">
        <Button
          variant="primary"
          disabled={!preview.total}
          loading={apply.isPending}
          onClick={() => apply.mutate(preview.id)}
        >
          {t('admin.productBulk.apply')}
        </Button>
        <HelpTip k="products.bulk_apply" />
      </span>
    </>
  ) : (
    <>
      <Button onClick={onClose}>{t('common.cancel')}</Button>
      <span className="inline-flex items-center gap-1">
        <Button variant="primary" loading={run.isPending} onClick={() => run.mutate(undefined)}>
          {t('admin.productBulk.preview')}
        </Button>
        <HelpTip k="products.bulk_preview" />
      </span>
    </>
  );

  return (
    <Dialog
      open
      variant="sheet"
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.productBulk.open')}
      description={t('admin.productBulk.lead')}
      closeLabel={t('common.close')}
      footer={footer}
    >
      {opId ? (
        !result.data || result.data.status === 'queued' || result.data.status === 'running' ? (
          <div className="space-y-3 text-sm">
            <p aria-live="polite">
              {t('admin.productBulk.queued', {
                processed: result.data?.processed ?? 0,
                total: result.data?.total ?? preview?.total ?? 0,
              })}
            </p>
            <progress
              className="h-2 w-full accent-[var(--zt-pine)]"
              max={Math.max(result.data?.total ?? 1, 1)}
              value={result.data?.processed ?? 0}
              aria-label={t('admin.productBulk.rows')}
            />
            <p className="text-ink-3">{t('admin.productBulk.queuedLead')}</p>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <p role="status" className="font-medium">
              {t('admin.productBulk.done', {
                applied: result.data.counts.applied ?? 0,
                skipped: result.data.counts.skipped ?? 0,
              })}
            </p>
            <ItemsTable
              kind={result.data.kind}
              items={result.data.items.filter((i) => i.state === 'skipped')}
              valueText={valueText}
              noteText={noteText}
            />
          </div>
        )
      ) : preview ? (
        <div className="space-y-4 text-sm">
          <p className="font-medium" role="status">
            {preview.total
              ? t('admin.productBulk.summary', {
                  products: preview.products,
                  total: preview.total,
                  skipped: preview.skipped,
                })
              : t('admin.productBulk.nothing')}
          </p>
          {preview.sample.length > 0 && (
            <div className="max-h-[22rem] overflow-auto rounded-md border border-line">
              <table className="w-full text-sm">
                <caption className="sr-only">{t(`admin.productBulk.kinds.${preview.kind}`)}</caption>
                <thead className="sticky top-0 bg-surface-2 text-ink-2">
                  <tr>
                    <th className="px-3 py-1.5 text-start font-medium">{t('admin.productBulk.item')}</th>
                    <th className="px-3 py-1.5 text-start font-medium">{t('admin.productBulk.before')}</th>
                    <th className="px-3 py-1.5 text-start font-medium">{t('admin.productBulk.after')}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.map((s, i) => (
                    <tr key={i} className="border-t border-line align-top">
                      <td className="px-3 py-1.5">{s.label}</td>
                      <td className="px-3 py-1.5 text-ink-3">{valueText(preview.kind, s.before)}</td>
                      <td className="px-3 py-1.5 font-medium">{valueText(preview.kind, s.after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview.total > preview.sample.length && (
            <p className="text-ink-3">{t('admin.productBulk.sampleNote')}</p>
          )}
          {preview.skippedSample.length > 0 && (
            <div>
              <p className="mb-1 font-medium">
                {t('admin.productBulk.skippedTitle', { count: preview.skipped })}
              </p>
              <ul className="max-h-40 space-y-0.5 overflow-auto text-ink-2">
                {preview.skippedSample.map((s, i) => (
                  <li key={i}>
                    {s.label}: <span className="text-ink-3">{noteText(s.note)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-ink-3">
            {t('admin.productBulk.expires', { time: formatDate(preview.expiresAt, true) })}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <Field label={t('admin.productBulk.kind')} help={<HelpTip k="products.bulk_kind" />}>
            {(p) => (
              <Select {...p} value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
                {kinds.map((k) => (
                  <option key={k} value={k}>
                    {t(`admin.productBulk.kinds.${k}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {kind === 'status' && (
            <Field label={t('admin.productBulk.newStatus')}>
              {(p) => (
                <Select {...p} value={status} onChange={(e) => setStatus(e.target.value)}>
                  {['draft', 'active', 'archived'].map((s) => (
                    <option key={s} value={s}>
                      {t(`admin.products.statuses.${s}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          {isCategory(kind) && (
            <fieldset>
              <legend className="mb-1.5 text-sm font-medium">{t('admin.productBulk.categories')}</legend>
              {!cats.data ? (
                <Spinner label={t('common.loading')} />
              ) : !cats.data.length ? (
                <p className="text-sm text-ink-3">{t('admin.products.noCategories')}</p>
              ) : (
                <div className="flex max-h-48 flex-wrap gap-x-5 gap-y-2 overflow-auto">
                  {cats.data.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="accent-[var(--zt-pine)]"
                        checked={categoryIds.includes(c.id)}
                        onChange={(e) =>
                          setCategoryIds(
                            e.target.checked ? [...categoryIds, c.id] : categoryIds.filter((x) => x !== c.id),
                          )
                        }
                      />
                      {c.name}
                    </label>
                  ))}
                </div>
              )}
              {err('categoryIds') && <p className="mt-1 text-sm text-rose">{err('categoryIds')}</p>}
            </fieldset>
          )}
          {isTags(kind) && (
            <Field
              label={t('admin.productBulk.tags')}
              description={t('admin.productBulk.tagsHint')}
              error={err('tags')}
            >
              {(p) => <Input {...p} value={tags} onChange={(e) => setTags(e.target.value)} />}
            </Field>
          )}
          {isCollection(kind) &&
            (cols.data && !manual.length ? (
              <p className="text-sm text-ink-3">{t('admin.productBulk.noManualCollections')}</p>
            ) : (
              <Field label={t('admin.productBulk.collection')} error={err('collectionId')}>
                {(p) => (
                  <Select {...p} value={collectionId} onChange={(e) => setCollectionId(e.target.value)}>
                    <option value="">{t('admin.productBulk.chooseCollection')}</option>
                    {manual.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            ))}
          {isStock(kind) && (
            <>
              <Field label={t('admin.productBulk.warehouse')} error={err('warehouseId')}>
                {(p) => (
                  <Select
                    {...p}
                    value={warehouseId || activeWh[0]?.id || ''}
                    onChange={(e) => setWarehouseId(e.target.value)}
                  >
                    {!activeWh.length && <option value="">{t('admin.productBulk.chooseWarehouse')}</option>}
                    {activeWh.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field
                label={
                  kind === 'stock_set'
                    ? t('admin.productBulk.quantitySet')
                    : t('admin.productBulk.quantityAdjust')
                }
                description={t('admin.productBulk.stockHint')}
                help={<HelpTip k="products.bulk_stock" />}
                error={err('quantity')}
              >
                {(p) => (
                  <Input
                    {...p}
                    inputMode="numeric"
                    className="w-40 tabular"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                  />
                )}
              </Field>
            </>
          )}
          {kind === 'seo' && (
            <>
              <p className="text-sm text-ink-3">{t('admin.productBulk.seoHint')}</p>
              <Field
                label={t('admin.productBulk.seoTitle')}
                help={<HelpTip k="products.bulk_seo" />}
                optionalLabel={t('common.optional')}
                error={err('seoTitle')}
              >
                {(p) => (
                  <Input
                    {...p}
                    value={seoTitle}
                    maxLength={120}
                    onChange={(e) => setSeoTitle(e.target.value)}
                  />
                )}
              </Field>
              <Field
                label={t('admin.productBulk.seoDescription')}
                optionalLabel={t('common.optional')}
                error={err('seoDescription')}
              >
                {(p) => (
                  <Input
                    {...p}
                    value={seoDescription}
                    maxLength={300}
                    onChange={(e) => setSeoDescription(e.target.value)}
                  />
                )}
              </Field>
              <div className="flex items-center gap-3">
                <Switch
                  label={t('admin.productBulk.onlyEmpty')}
                  checked={onlyEmpty}
                  onCheckedChange={setOnlyEmpty}
                />
                <span className="text-sm">{t('admin.productBulk.onlyEmpty')}</span>
              </div>
            </>
          )}
          {isListing(kind) && (
            <Field
              label={t('admin.productBulk.store')}
              description={kind === 'store_publish' ? t('admin.productBulk.storeHint') : undefined}
              help={<HelpTip k="products.bulk_store" />}
              error={err('storeId')}
            >
              {(p) => (
                <Select
                  {...p}
                  value={storeId || storeChoice.storeId}
                  onChange={(e) => setStoreId(e.target.value)}
                >
                  {storeChoice.stores.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          {kind === 'merchant' && (
            <Field label={t('admin.productBulk.merchantChoice')}>
              {(p) => (
                <Select
                  {...p}
                  value={include ? 'include' : 'exclude'}
                  onChange={(e) => setInclude(e.target.value === 'include')}
                >
                  <option value="include">{t('admin.productBulk.merchantOptions.include')}</option>
                  <option value="exclude">{t('admin.productBulk.merchantOptions.exclude')}</option>
                </Select>
              )}
            </Field>
          )}
        </div>
      )}
    </Dialog>
  );
}

function ItemsTable({
  kind,
  items,
  valueText,
  noteText,
  withState,
}: {
  kind: string;
  items: ProductBulkDetail['items'];
  valueText: (kind: string, v: unknown) => string;
  noteText: (n: string | null) => string;
  withState?: boolean;
}) {
  const t = useT();
  if (!items.length) return null;
  return (
    <div className="max-h-[24rem] overflow-auto rounded-md border border-line">
      <table className="w-full text-sm">
        <caption className="sr-only">{t(`admin.productBulk.kinds.${kind}`)}</caption>
        <thead className="sticky top-0 bg-surface-2 text-ink-2">
          <tr>
            <th className="px-3 py-1.5 text-start font-medium">{t('admin.productBulk.item')}</th>
            <th className="px-3 py-1.5 text-start font-medium">{t('admin.productBulk.before')}</th>
            <th className="px-3 py-1.5 text-start font-medium">{t('admin.productBulk.after')}</th>
            {withState && (
              <th className="px-3 py-1.5 text-start font-medium">{t('admin.productBulk.status')}</th>
            )}
            <th className="px-3 py-1.5 text-start font-medium">{t('admin.productBulk.note')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.id} className="border-t border-line align-top">
              <td className="px-3 py-1.5">{i.label}</td>
              <td className="px-3 py-1.5 text-ink-3">{valueText(kind, i.before)}</td>
              <td className="px-3 py-1.5">{valueText(kind, i.after)}</td>
              {withState && (
                <td className="px-3 py-1.5">
                  <Badge tone={itemTone[i.state]}>{t(`admin.productBulk.states.${i.state}`)}</Badge>
                </td>
              )}
              <td className="px-3 py-1.5 text-ink-3">{noteText(i.note)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Geçmiş ekranı
// ---------------------------------------------------------------------------

export function ProductBulkPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const kinds = useBulkKinds();
  const canApply = usePermissions().can('catalog.bulk.apply');
  const [detailId, setDetailId] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['product-bulk'],
    queryFn: () => api.get<ProductBulkOperation[]>('/v1/admin/products/bulk'),
    refetchInterval: (q) =>
      q.state.data?.some((o) => o.status === 'queued' || o.status === 'running') ? 2000 : false,
  });
  const revert = useApiMutation(
    (id: string) =>
      api.post<{ reverted: number; skipped: number }>(`/v1/admin/products/bulk/${id}/revert`, undefined, {
        idempotencyKey: newIdempotencyKey(),
      }),
    {
      invalidate: [['product-bulk'], ['products']],
      success: (r) => t('admin.productBulk.reverted', r),
    },
  );
  const rows = (list.data ?? []).filter((o) => o.status !== 'preview' && o.status !== 'expired');
  return (
    <PageFrame helpKey="page.product_bulk">
      {list.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : !rows.length ? (
        <EmptyState title={t('admin.productBulk.empty')} description={t('admin.productBulk.emptyLead')} />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[40rem] text-sm">
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.productBulk.date')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.productBulk.kind')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.productBulk.status')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.productBulk.rows')}</th>
                <th className="px-3 py-2">
                  <span className="sr-only">{t('admin.productBulk.details')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} className="border-t border-line">
                  <td className="px-3 py-2 tabular">{formatDate(o.createdAt, true)}</td>
                  <td className="px-3 py-2">{t(`admin.productBulk.kinds.${o.kind}`)}</td>
                  <td className="px-3 py-2">
                    <Badge tone={opTone[o.status]}>{t(`admin.productBulk.statuses.${o.status}`)}</Badge>
                  </td>
                  <td className="px-3 py-2 text-end tabular">
                    {t('admin.productBulk.progress', { processed: o.processed, total: o.total })}
                    {o.skipped > 0 && (
                      <span className="block text-ink-3">
                        {t('admin.productBulk.skippedCount', { count: o.skipped })}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setDetailId(o.id)}>
                        {t('admin.productBulk.details')}
                      </Button>
                      {o.status === 'applied' &&
                        (o.kind === 'cells' ? canApply : kinds.includes(o.kind as Kind)) && (
                          <span className="inline-flex items-center gap-1">
                            <ConfirmButton
                              variant="secondary"
                              message={t('admin.productBulk.revertConfirm')}
                              onConfirm={() => revert.mutateAsync(o.id)}
                            >
                              {t('admin.productBulk.revert')}
                            </ConfirmButton>
                            <HelpTip k="products.bulk_revert" />
                          </span>
                        )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detailId && <BulkDetailDialog id={detailId} onClose={() => setDetailId(null)} />}
    </PageFrame>
  );
}

function BulkDetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const t = useT();
  const { formatDate } = useI18n();
  const valueText = useValueText();
  const noteText = useNoteText();
  const d = useQuery({
    queryKey: ['product-bulk', id],
    queryFn: () => api.get<ProductBulkDetail>(`/v1/admin/products/bulk/${id}`),
  });
  return (
    <Dialog
      open
      variant="sheet"
      onOpenChange={(v) => !v && onClose()}
      title={d.data ? t(`admin.productBulk.kinds.${d.data.kind}`) : t('admin.productBulk.details')}
      description={d.data ? formatDate(d.data.createdAt, true) : undefined}
      closeLabel={t('common.close')}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}
    >
      {!d.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap gap-2">
            <Badge tone={opTone[d.data.status]}>{t(`admin.productBulk.statuses.${d.data.status}`)}</Badge>
            {Object.entries(d.data.counts).map(([state, n]) => (
              <Badge key={state} tone={itemTone[state]}>
                {t(`admin.productBulk.states.${state}`)}: {n}
              </Badge>
            ))}
          </div>
          <ItemsTable
            kind={d.data.kind}
            items={d.data.items}
            valueText={valueText}
            noteText={noteText}
            withState
          />
          {d.data.total > d.data.items.length && (
            <p className="text-ink-3">{t('admin.productBulk.itemsNote')}</p>
          )}
        </div>
      )}
    </Dialog>
  );
}
